import { MenuClass, MenuHolderClass, MenuWidgetClass } from './constants';
import { getEventNodeProps } from './viewWatcher';
import { REPEATER, CTRL_ID, XMLACTION, XCTNAME } from './constantsFormAttributes';
import { ACTION_COLLECTION_INSERT, ACTION_OPTIONAL_INSERT } from '../../Manifest/modules/editableComponentActions';
import { COLLECTION_COMPONENT_TYPE, OPTIONAL_COMPONENT_TYPE } from '../../Manifest/modules/editableComponentTypes';
import { both, isEmpty } from 'ramda';

const $ = jQuery;

const NOMENU_KEY = 'fv-nomenu';

// MenuHandler - Responsible for handling events related to view menus and 
//               updating the HTML view accordingly

function getRepeaterFromEventTarget($target) {
    return $target.closest(REPEATER)[0];
}

function getPropsForRepeater(repeaterNode) {
    let props = getEventNodeProps(repeaterNode);

    // The ctrlId and control name will not be present on repeating table <tr> nodes, so we need to go higher up to retrive it
    props.ctrlId = props.ctrlId || $(repeaterNode).closest("table").attr(CTRL_ID);
    props.controlType = props.controlType || $(repeaterNode).closest('tbody').attr(XCTNAME);

    return props;
}

function shouldShowInMenu(item) {
    return item.action !== ACTION_COLLECTION_INSERT && item.action !== ACTION_OPTIONAL_INSERT;
}

const xmlToEditTypeMatches = (controlProps) => (xmlToEdit) => {
    switch (controlProps.controlType.toLowerCase()) {
        case 'repeatingtable':
        case 'repeatingsection':
            return xmlToEdit.component === COLLECTION_COMPONENT_TYPE;
        case 'section':
            return xmlToEdit.component === OPTIONAL_COMPONENT_TYPE;
        default:
            return false;
    }
};

// viewContext is used as a differentiator if there are multiple controls bound to the same node
// it can be undefined if there is no ambiguity
const ctrlIdMatches = (controlProps) => (xmlToEdit) =>
    !xmlToEdit.viewContext || xmlToEdit.viewContext === controlProps.ctrlId;

// ControlProps -> XmlToEdit -> Boolean
const xmlToEditMatchesControl = (controlProps) =>
    both(xmlToEditTypeMatches(controlProps), ctrlIdMatches(controlProps));

function widgetManager(template, viewManager, domNodeProvider) {

    function getEditableComponentForEventProps(props, targetDsNode) {
        const domNode = targetDsNode && targetDsNode.getNode();
        const xmlToEdits = domNode && template.getXmlToEdits(domNode, viewManager.getCurrentView());

        return xmlToEdits && xmlToEdits.find(xmlToEditMatchesControl(props));
    }

    // DomNode -> XmlToEdit
    // Retrieves the XmlToEdit object corresponding to the provided repeating control HTML DOM node
    function getEditableComponentForRepeater(repeaterNode) {
        const props = getPropsForRepeater(repeaterNode);
        const targetDomNode = domNodeProvider.getNodeForNodeId(props.nodeId);

        return getEditableComponentForEventProps(props, targetDomNode);
    }

    function getMenuItems($widget) {
        const repeaterNode = getRepeaterFromEventTarget($widget);
        const editableComponent = repeaterNode && getEditableComponentForRepeater(repeaterNode);
        const menuItems = editableComponent && template.getContextMenuItems(viewManager.getCurrentView(), editableComponent.name);

        return menuItems;
    }

    const appendMenuItem = ($menu, item) => {
        const $li = $("<li>").attr(XMLACTION, item.action).text(item.caption);
        $menu.append($li);
    };

    // jQuery -> Boolean
    // Adds the widget menu to the specified menu widget dom node.
    // Returns true if there are menu items to show, or false otherwise
    function appendWidgetMenu($widget) {
        const $menu = $('<ul class="fv-menu" />');
        const menuItems = (getMenuItems($widget) || []).filter(shouldShowInMenu);

        if (isEmpty(menuItems)) {
            $widget.data(NOMENU_KEY, true);

            // assume no menu for this particular repeater
            return false;
        }

        for (const menuItem of menuItems) {
            appendMenuItem($menu, menuItem);
        }

        $('<div class="fv-menu-holder" />')
            .append($menu)
            .appendTo($widget);

        return true;
    }

    function showWidgetMenu(eventInfo) {
        const targ = $(eventInfo.target);
        const myMenu = targ.find(MenuHolderClass);
        const allMenuWidgets = $(MenuWidgetClass);
        const allMenus = allMenuWidgets.find(MenuHolderClass);

        allMenus.not(myMenu)
            .add(allMenuWidgets.not(targ))
            .hide();

        myMenu.toggle();
    }

    // Ensures that there is a menu attatched to the targ DOM node,
    // creating one if necessary
    function ensureMenu($targ) {
        const menu = $targ.find(MenuClass);

        if (menu.length) {
            return true;
        }

        if ($targ.data(NOMENU_KEY)){
            return false;
        }
        
        return appendWidgetMenu($targ);
    }

    const countParents = (node) => $(node).parents().length;

    function showDropdownWidget(eventInfo) {
        const $targ = $(eventInfo.target);
        const widgetHolders = $(getRepeaterFromEventTarget($targ))
            .find(".fv-menu-widget-holder");

        // find the widget holder for the outermost repeating section of the ones selected,
        // in case there are others nested inside
        const outerWidgetHolder = widgetHolders
            .toArray()
            .sort((a, b) => countParents(a) - countParents(b))[0];


        const toShow = $(outerWidgetHolder || [])
            .children(MenuWidgetClass);

        // hide all menus except the current one
        $(MenuWidgetClass).not(toShow).hide();

        if (ensureMenu(toShow)) {
            toShow.show();
        }
    }

    // Duplicated from ViewWatcher - reconcile this
    function processMenuItemClick(eventInfo) {
        var targ = $(eventInfo.target),
            action = eventInfo.action,
            repeater = getRepeaterFromEventTarget(targ),
            props = repeater && getPropsForRepeater(repeater),
            targetDomNode = props && domNodeProvider.getNodeForNodeId(props.nodeId),
            xmlToEdit = targetDomNode && getEditableComponentForEventProps(props, targetDomNode);

        if (xmlToEdit) {
            return { target: targetDomNode, action: action, xmlToEdit: xmlToEdit };
        } else {
            throw Error("Unable to carry out the specified action.");
        }
    }

    return {
        showDropdownWidget: showDropdownWidget,
        showWidgetMenu: showWidgetMenu,
        processMenuItemClick: processMenuItemClick,
    };
}

export default widgetManager;
